import type { AxiosResponse } from 'axios';
import type {
  ApiResponse,
  AuditLogEntry,
  Cliente,
  ClienteStats,
  PaginatedResponse,
} from '@offertify/core';
import { getApiClient } from '../configure';

export interface GetClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  inattivi_giorni?: number;
  solo_generici?: boolean;
  agente_id?: string[];
  tipo?: string;
  has_portal_access?: boolean;
  canale_id?: string[];
}

export interface ImportFromFICResult {
  success: boolean;
  message: string;
  created: number;
  updated: number;
  total_from_fic: number;
}

/** Resolve the shared transport lazily so the app can configure it at startup. */
function http() {
  return getApiClient().client;
}

/**
 * Clienti domain endpoints. Split out of the web api.ts monolith; identical
 * routes and payloads, now platform-agnostic (web + mobile).
 */
export const clientsAPI = {
  getClients: (params?: GetClientsParams): Promise<AxiosResponse<PaginatedResponse<Cliente>>> =>
    http().get('/clienti', { params, paramsSerializer: { indexes: null } }),

  getClientsForSelect: (): Promise<AxiosResponse<Cliente[]>> => http().get('/clienti/select'),

  getClient: (id: string): Promise<AxiosResponse<Cliente>> => http().get(`/clienti/${id}`),

  createClient: (data: Partial<Cliente>): Promise<AxiosResponse<Cliente>> =>
    http().post('/clienti', data),

  updateClient: (id: string, data: Partial<Cliente>): Promise<AxiosResponse<Cliente>> =>
    http().put(`/clienti/${id}`, data),

  // Soft delete (backend marks is_deleted; never a physical delete).
  deleteClient: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    http().delete(`/clienti/${id}`),

  importFromFIC: (): Promise<AxiosResponse<ImportFromFICResult>> => http().post('/clienti/import-fic'),

  getClientStats: (id: string): Promise<AxiosResponse<ClienteStats>> =>
    http().get(`/clienti/${id}/stats`),

  getClientTimeline: (
    id: string,
    params?: { page?: number; limit?: number },
  ): Promise<AxiosResponse<PaginatedResponse<AuditLogEntry>>> =>
    http().get(`/clienti/${id}/timeline`, { params }),

  inviteToPortal: (id: string, data?: { email_override?: string; agente_id?: string }) =>
    http().post<Cliente>(`/clienti/${id}/invite`, data || {}),

  revokeAccess: (id: string) => http().post<Cliente>(`/clienti/${id}/revoke-access`),

  activatePortal: (
    id: string,
    data: { password: string; email_override?: string; agente_id?: string },
  ) => http().post<Cliente>(`/clienti/${id}/activate-portal`, data),

  deactivatePortal: (id: string) => http().post<Cliente>(`/clienti/${id}/deactivate-portal`),

  reactivatePortal: (id: string) => http().post<Cliente>(`/clienti/${id}/reactivate-portal`),

  resetPortalPassword: (id: string, data: { new_password: string }) =>
    http().post<Cliente>(`/clienti/${id}/reset-portal-password`, data),

  impersonateClient: (id: string) => http().post(`/clienti/${id}/impersonate`),
};
